import { getPostApi } from '@/api-calls/posts/get'
import { updatePostMetadataApi } from '@/api-calls/posts/mutate'
import { ContentEditGuard } from '@/components/guards/content-edit-guard'
import Container from '@/components/header/container'
import { postMetadataSchema } from '@/lib/schema/posts'
import { onUpload } from '@/lib/image/uploader'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Skeleton,
  Textarea,
} from '@blogging/ui'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { ArrowLeft, ImagePlus, Loader2, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const emptyValues = {
  metaTitle: '',
  metaDescription: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  twitterTitle: '',
  twitterDescription: '',
  twitterImage: '',
}

function ImageUploadField({
  name,
  label,
  description,
}: {
  name: 'ogImage' | 'twitterImage'
  label: string
  description: string
}) {
  const { control, setValue: setFormValue } = useFormContext()
  const [isUploading, setIsUploading] = useState(false)
  const inputId = `${name}-upload`
  const setValue = (fieldName: string, value: string) =>
    setFormValue(fieldName, value, { shouldDirty: true })

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      const url = (await onUpload(file)) as string
      if (url) setValue(name, url)
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload image')
    } finally {
      setIsUploading(false)
      e.target.value = ''
    }
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          {field.value ? (
            <div className="relative max-w-md">
              <img
                src={field.value}
                alt={`${label} preview`}
                className="w-full aspect-[1200/630] object-cover rounded-lg border"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="absolute top-2 right-2 h-8 w-8 p-0"
                onClick={() => setValue(name, '')}
                aria-label={`Remove ${label.toLowerCase()}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <FormControl>
              <label
                htmlFor={inputId}
                className="flex max-w-md cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-8 text-muted-foreground hover:bg-muted/50"
              >
                {isUploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ImagePlus className="h-5 w-5" />
                )}
                <span className="text-sm">
                  {isUploading ? 'Uploading...' : 'Upload an image'}
                </span>
                <input
                  id={inputId}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </label>
            </FormControl>
          )}
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export function PostMetadataPage({ type }: { type: 'post' | 'page' }) {
  const { siteId, id } = useParams()
  const queryClient = useQueryClient()

  const form = useForm({
    resolver: zodResolver(postMetadataSchema),
    defaultValues: emptyValues,
  })

  const {
    data: post,
    isLoading,
    isError,
  } = useFetch(getPostApi, {
    resourceName: 'post',
    initialParams: { siteId: siteId as string, postId: id as string },
  })

  // Populate form once the post (and its metadata) loads
  useEffect(() => {
    if (post && !isLoading) {
      const meta = post.postsMeta
      form.reset({
        metaTitle: meta?.metaTitle ?? '',
        metaDescription: meta?.metaDescription ?? '',
        ogTitle: meta?.ogTitle ?? '',
        ogDescription: meta?.ogDescription ?? '',
        ogImage: meta?.ogImage ?? '',
        twitterTitle: meta?.twitterTitle ?? '',
        twitterDescription: meta?.twitterDescription ?? '',
        twitterImage: meta?.twitterImage ?? '',
      })
    }
  }, [post, isLoading, form])

  const { mutate: updateMetadata, isPending: isUpdating } = useMutation(
    updatePostMetadataApi,
    {
      resourceName: 'postMeta',
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [getPostApi.apiName, { siteId, postId: id }],
        })
        form.reset(form.getValues())
        toast.success('Metadata updated successfully!')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update metadata')
      },
    },
  )

  const onSubmit = (data: any) => {
    updateMetadata({
      id: id as string,
      siteId: siteId as string,
      ...data,
    })
  }

  const editorPath = `/${siteId}/editor/${type}/${id}`

  if (isLoading) {
    return (
      <Container>
        <div className="mx-auto max-w-3xl py-10">
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </Container>
    )
  }

  if (isError || !post) {
    return (
      <Container>
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="text-center">
            <h3 className="text-lg font-semibold">
              {isError ? 'Error loading post' : 'Post not found'}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {isError
                ? 'Please try refreshing the page'
                : "The post you're looking for doesn't exist"}
            </p>
          </div>
        </div>
      </Container>
    )
  }

  return (
    <ContentEditGuard
      contentOwnerId={post.createdById}
      contentType={type}
      isLoading={isLoading}
    >
      <Container>
        <div className="mx-auto max-w-3xl space-y-8 py-10">
          <div className="flex flex-col space-y-2">
            <Link
              to={editorPath}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to editor
            </Link>
            <h1 className="text-3xl font-bold">Metadata</h1>
            <p className="text-muted-foreground">
              Customize how <span className="font-medium">{post.title}</span>{' '}
              appears on search engines, AI assistants, and social networks.
              Empty fields fall back to the {type} title and excerpt.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
              {/* Search engines & AI */}
              <div className="space-y-6 rounded-lg border bg-card p-6">
                <div>
                  <h3 className="text-lg font-semibold">
                    Search engines &amp; AI
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Used by Google, Bing, and AI assistants when indexing this{' '}
                    {type}.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="metaTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta title</FormLabel>
                      <FormControl>
                        <Input placeholder={post.title} {...field} />
                      </FormControl>
                      <FormDescription>
                        Recommended: under 60 characters.{' '}
                        {field.value ? `${field.value.length} characters.` : ''}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="metaDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meta description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={
                            post.excerpt ||
                            'A compelling description for search results...'
                          }
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Recommended: under 155 characters.{' '}
                        {field.value ? `${field.value.length} characters.` : ''}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* X card */}
              <div className="space-y-6 rounded-lg border bg-card p-6">
                <div>
                  <h3 className="text-lg font-semibold">X card</h3>
                  <p className="text-sm text-muted-foreground">
                    Customize the preview shown when this {type} is shared on X
                    (Twitter).
                  </p>
                </div>

                <ImageUploadField
                  name="twitterImage"
                  label="X image"
                  description="Recommended size: 1200 x 630 pixels. Falls back to the feature image."
                />

                <FormField
                  control={form.control}
                  name="twitterTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>X title</FormLabel>
                      <FormControl>
                        <Input placeholder={post.title} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="twitterDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>X description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={post.excerpt || ''}
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Facebook & LinkedIn card */}
              <div className="space-y-6 rounded-lg border bg-card p-6">
                <div>
                  <h3 className="text-lg font-semibold">
                    Facebook &amp; LinkedIn card
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Customize the Open Graph preview used by Facebook, LinkedIn,
                    and most other platforms.
                  </p>
                </div>

                <ImageUploadField
                  name="ogImage"
                  label="Facebook & LinkedIn image"
                  description="Recommended size: 1200 x 630 pixels. Falls back to the feature image."
                />

                <FormField
                  control={form.control}
                  name="ogTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Facebook &amp; LinkedIn title</FormLabel>
                      <FormControl>
                        <Input placeholder={post.title} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ogDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Facebook &amp; LinkedIn description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={post.excerpt || ''}
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col items-start space-y-4 rounded-lg border bg-muted/50 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                <div className="flex-1">
                  <p className="text-sm font-medium">Save your changes</p>
                  <p className="text-sm text-muted-foreground">
                    Make sure to save your changes before leaving this page.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={isUpdating || !form.formState.isDirty}
                  className="w-full sm:w-auto"
                >
                  {isUpdating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </Container>
    </ContentEditGuard>
  )
}
